import React from 'react';
import { useState, useEffect, useCallback } from 'react';
import jstz from 'jstimezonedetect';

import {
    DIRECTORY_TYPES,
    FEATURE_FLAGS_NAMES,
    HTTP_OPTIONS,
    INFINITE_SCROLL_BATCH_SIZE,
    INVESTIGATION_ENTITY_TYPES
} from '@silverfort-nodelibs/common/utils/consts';
import { isFeatureActive } from '@silverfort-nodelibs/common/utils/common';

import InvestigationTimeline from './InvestigationTimeline';
import InvestigationMap from './InvestigationMap';
import EntityActivitiesFilters from './EntityActivitiesFilters';
import useFeature from '../../../SystemSettings/hooks/featureFlags/useFeature';
import styles from './EntityActivities.module.css';
import exportIcon from '../../../common/Images/export.png';
import { entityActivitiesExport } from '../../InvestigationUtils';
import { httpGet } from '../../../utils';
import Loader from '../../../Controls/Loader/Loader';
import { destination, source, users } from '../../../Controls/Filters/FiltersObjects';
import { useAppDrawer } from '../../../AppDrawer/AppDrawerProvider';
import InvestigationDrawer from '../../../InvestigationDrawer/InvestigationDrawer';

const EntityActivities = ({
    entityGuid,
    entityId,
    entityType,
    user,
    hideRightMargin = false,
    mainEntityGuid = null
}) => {
    const [filters, setFilters] = useState({});
    const [entityActivities, setEntityActivities] = useState([]);
    const [locationActivities, setLocationActivities] = useState([]);
    const [lastAuthIdentity, setLastAuthIdentity] = useState();
    const [lastRiskIdentity, setLastRiskIdentity] = useState();
    const [lastIncidentChangeIdentity, setLastIncidentChangeIdentity] = useState();
    const [relyingParties, setRelyingParties] = useState({});
    const [hasMoreActivities, setHasMoreActivities] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [chosenLocation, setChosenLocation] = useState({});
    const [metadataForLogs, setMetadataForLogs] = useState({});
    const [defaultDestinations, setDefaultDestinations] = useState({});
    const geoLocationEnabled = useFeature(FEATURE_FLAGS_NAMES.GeoLocationEnabled);
    const investigationExpenderEnabled = useFeature(FEATURE_FLAGS_NAMES.InvestigationExpenderEnabled);
    const { displayContent } = useAppDrawer();

    const handleInvestigationClick = (guid, identifier, adType) => {
        displayContent(<InvestigationDrawer guid={guid} identifier={identifier} adType={adType} />);
    };

    const formatFilters = (filters) => {
        const formattedFilters = {};

        for (const filterId in filters) {
            if (filterId === source.id) {
                formattedFilters.includeSources = filters[source.id]?.map((source) => source.label);
            } else if (filterId === destination.id) {
                formattedFilters.includeDestinations = filters[destination.id]?.map((dest) => dest.value);
            } else if (filterId === users.id) {
                formattedFilters.includeUsers = filters[users.id]?.map((user) => user.value);
            } else {
                formattedFilters[filterId] = filters[filterId];
            }
        }

        return formattedFilters;
    };

    const getEntityActivities = useCallback(
        async (firstLoading, loading, lastAuth, lastRisk, lastIncidentChange) => {
            if (loading) {
                return;
            }
            if (firstLoading) {
                setIsLoading(true);
            }
            const response = await httpGet('/investigate/getMetadataForLogs', { [HTTP_OPTIONS.HANDLE_ERRORS]: true });

            if (response && response.data) {
                setMetadataForLogs({ ...response.data, user: user });
            }

            let params = {
                entityType: entityType.id,
                lastAuthIdentity: firstLoading ? null : lastAuth,
                localTimeZone: encodeURIComponent(jstz.determine().name()),
                filters: formatFilters(filters)
            };

            let requestUrl = '';
            if (entityType.id === INVESTIGATION_ENTITY_TYPES.INCIDENT.id) {
                requestUrl = `/incidents/${entityId}/activities`;
                params = {
                    ...params,
                    incidentId: entityId,
                    lastRiskIdentity: firstLoading ? null : lastRisk,
                    lastIncidentChangeIdentity: firstLoading ? null : lastIncidentChange
                };
                if (mainEntityGuid) {
                    params.mainEntityGuid = mainEntityGuid;
                }
            } else {
                requestUrl = '/investigate/activities';
                params = {
                    ...params,
                    entityGuid: entityGuid,
                    entityId: entityId,
                    lastRiskIdentity: firstLoading ? null : lastRisk
                };
            }

            try {
                const response = await httpGet(requestUrl, { [HTTP_OPTIONS.REQ_OPTIONS]: { params: params } });
                const data = response.data;

                if (data) {
                    if (entityType.id === INVESTIGATION_ENTITY_TYPES.INCIDENT.id) {
                        setEntityActivities((activities) =>
                            firstLoading ? data.activity : [...activities, ...data.activity]
                        );
                        setDefaultDestinations(data.defaultDestinations);
                        setLastAuthIdentity(data.lastIdentity);
                        setLastRiskIdentity(data.lastRiskIdentity);
                        setLastIncidentChangeIdentity(data.lastIncidentChangeIdentity);
                        setHasMoreActivities(data.activity.length === INFINITE_SCROLL_BATCH_SIZE);
                    }
                    if (data.activity) {
                        setDefaultDestinations(data.defaultDestinations);
                        setEntityActivities((activities) =>
                            firstLoading ? data.activity : [...activities, ...data.activity]
                        );
                        addLocationActivities(data.activity, firstLoading);
                        setLastAuthIdentity(data.lastAuthIdentity);
                        setLastRiskIdentity(data.lastRiskIdentity);
                        setHasMoreActivities(data.activity.length === INFINITE_SCROLL_BATCH_SIZE);
                        setRelyingParties(data.relyingParties);
                    }
                }
                setIsLoading(false);
            } catch (err) {
                console.error(err);
            }
        },
        [entityId, entityGuid, entityType, filters, user, mainEntityGuid]
    );

    const addLocationActivities = (newActivities, firstLoading) => {
        const newPoints = [];
        if (newActivities && newActivities.length > 0) {
            newActivities.forEach((authLog) => {
                if (
                    ('geo_device_longitude' in authLog &&
                        authLog.geo_device_longitude &&
                        authLog.geo_device_longitude.toString() !== 0) ||
                    ('geo_device_latitude' in authLog &&
                        authLog.geo_device_latitude &&
                        authLog.geo_device_latitude.toString() !== 0)
                ) {
                    const directoryType =
                        authLog.directory_type && DIRECTORY_TYPES[authLog.directory_type]
                            ? DIRECTORY_TYPES[authLog.directory_type].displayName
                            : DIRECTORY_TYPES.ad.displayName;

                    newPoints.push({
                        type: 'Feature',
                        properties: {
                            cluster: false,
                            authID: authLog.event_id,
                            category: 'device',
                            directoryType: directoryType,
                            ...authLog
                        },
                        geometry: {
                            type: 'Point',
                            coordinates: [
                                parseFloat(authLog.geo_device_longitude),
                                parseFloat(authLog.geo_device_latitude)
                            ]
                        }
                    });
                }
            });
        }
        setLocationActivities((lA) => (firstLoading ? newPoints : [...lA, ...newPoints]));
    };

    useEffect(() => {
        if (entityId !== null) {
            getEntityActivities(true);
        }
    }, [entityId, getEntityActivities]);

    const exportEntityActivities = async () => {
        const params = {
            entityGuid: entityGuid,
            entityId: entityId,
            entityType: entityType.id,
            localTimeZone: encodeURIComponent(jstz.determine().name()),
            filters: formatFilters(filters),
            isExport: true
        };
        try {
            const response = await httpGet('/investigate/activities', {
                [HTTP_OPTIONS.REQ_OPTIONS]: { params: params }
            });
            const data = response.data;
            entityActivitiesExport(
                data && data.activity ? data.activity : [],
                data.rulesNames,
                data.logsDisplayAccountType,
                data.groupPolicies
            );
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <div className={styles.warper}>
            <div className={styles.upSide}>
                {entityType.id !== INVESTIGATION_ENTITY_TYPES.INCIDENT.id && (
                    <button className={styles.exportButton} onClick={exportEntityActivities} title="Export">
                        <img src={exportIcon} alt="" />
                    </button>
                )}
                <EntityActivitiesFilters
                    geoLocationEnabled={geoLocationEnabled}
                    entityType={entityType}
                    onFiltersChanged={setFilters}
                />
            </div>
            <div
                className={`${styles.bottomPane} ${
                    entityType.id === INVESTIGATION_ENTITY_TYPES.INCIDENT.id ? styles.noMap : ''
                }`}>
                <div className={styles.timeline}>
                    {isLoading ? (
                        <div className={styles.loader}>
                            <Loader darkTheme={true} />
                        </div>
                    ) : (
                        <InvestigationTimeline
                            entityActivity={entityActivities}
                            entityType={entityType}
                            entityID={entityId}
                            entityGuid={entityGuid}
                            loadActivityData={() =>
                                getEntityActivities(
                                    false,
                                    isLoading,
                                    lastAuthIdentity,
                                    lastRiskIdentity,
                                    lastIncidentChangeIdentity
                                )
                            }
                            hasMoreActivity={hasMoreActivities}
                            relyingParties={relyingParties}
                            setLocation={setChosenLocation}
                            geoLocationEnabled={
                                isFeatureActive(geoLocationEnabled) &&
                                entityType.id !== INVESTIGATION_ENTITY_TYPES.INCIDENT.id
                            }
                            investigationExpenderEnabled={isFeatureActive(investigationExpenderEnabled)}
                            darkBackground={entityType.id === INVESTIGATION_ENTITY_TYPES.INCIDENT.id}
                            metadataForLogs={metadataForLogs}
                            defaultDestinations={defaultDestinations}
                            hideRightMargin={hideRightMargin}
                            handleInvestigationClick={handleInvestigationClick}
                        />
                    )}
                </div>
                {entityType.id !== INVESTIGATION_ENTITY_TYPES.INCIDENT.id && (
                    <div className={styles.map}>
                        <InvestigationMap points={locationActivities} chosenLocation={chosenLocation} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default EntityActivities;
